import React, { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save, Loader2, Palette } from "lucide-react";
import MediaUploadInput from "@/components/MediaUploadInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_BRANDING = {
  app_name: "WA Bot", logo_url: "", primary_color: "#0f172a",
  secondary_color: "#f1f5f9", accent_color: "#22c55e",
  login_title: "Welcome back", login_subtitle: "Sign in to your WhatsApp Bot Dashboard",
  sidebar_title: "WA Bot", dashboard_welcome_text: "Here's an overview of your WhatsApp Bot workspace",
};

const fields = [
  { key: "app_name", label: "App Name", type: "text" },
  { key: "primary_color", label: "Primary Color", type: "color" },
  { key: "secondary_color", label: "Secondary Color", type: "color" },
  { key: "accent_color", label: "Accent Color", type: "color" },
  { key: "login_title", label: "Login Title", type: "text" },
  { key: "login_subtitle", label: "Login Subtitle", type: "text" },
  { key: "sidebar_title", label: "Sidebar Title", type: "text" },
  { key: "dashboard_welcome_text", label: "Dashboard Welcome Text", type: "text" },
];

export default function AdminBranding() {
  const { getAdminAxios } = useOutletContext();
  const adminAxios = getAdminAxios();
  const [branding, setBranding] = useState({ ...DEFAULT_BRANDING });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    adminAxios.get(`${API}/admin/branding`)
      .then(({ data }) => setBranding({ ...DEFAULT_BRANDING, ...data }))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await adminAxios.put(`${API}/admin/branding`, branding);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-white/5 rounded w-48" />
          <div className="h-64 bg-white/5 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-3xl" data-testid="admin-branding-page">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-white flex items-center gap-2">
          <Palette className="w-6 h-6 text-amber-400" /> Global Branding
        </h1>
        <p className="text-sm text-slate-400 mt-1">Default branding applied to all workspaces (unless overridden per-client)</p>
      </div>

      <Card className="bg-[#13112b] border-indigo-900/30">
        <CardHeader>
          <CardTitle className="text-base text-white">Branding Settings</CardTitle>
          <CardDescription className="text-slate-400">These defaults apply to all clients without custom branding</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Preview */}
          <div className="rounded-lg p-4 border border-white/10 mb-4" style={{ backgroundColor: branding.primary_color || "#0f172a" }}>
            <div className="flex items-center gap-2 mb-2">
              {branding.logo_url ? (
                <img src={branding.logo_url} alt="" className="w-6 h-6 rounded object-cover" />
              ) : (
                <div className="w-6 h-6 rounded" style={{ backgroundColor: branding.accent_color || "#22c55e" }} />
              )}
              <span className="text-white text-sm font-medium">{branding.sidebar_title || "WA Bot"}</span>
            </div>
            <p className="text-white/60 text-xs">Preview of sidebar appearance</p>
          </div>

          {/* Logo with drag-and-drop (URL input + Upload button + drop zone) */}
          <div className="space-y-2">
            <Label className="text-slate-300">Logo (arrastrá una imagen, hacé click o pegá una URL)</Label>
            <MediaUploadInput
              value={branding.logo_url || ""}
              onChange={(url) => setBranding({ ...branding, logo_url: url })}
              uploadEndpoint="/admin/upload"
              uploadAxios={adminAxios}
              accept="image/*"
              testIdPrefix="global-branding-logo-upload"
              placeholder="https://... o arrastrá una imagen"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            {fields.map((f) => (
              <div key={f.key} className="space-y-2">
                <Label className="text-slate-300">{f.label}</Label>
                {f.type === "color" ? (
                  <div className="flex gap-2 items-center">
                    <input type="color" value={branding[f.key] || "#000000"} onChange={(e) => setBranding({ ...branding, [f.key]: e.target.value })} className="w-10 h-9 rounded border border-white/10 bg-transparent cursor-pointer" data-testid={`global-branding-${f.key}`} />
                    <Input value={branding[f.key] || ""} onChange={(e) => setBranding({ ...branding, [f.key]: e.target.value })} className="bg-white/5 border-white/10 text-white flex-1" />
                  </div>
                ) : (
                  <Input value={branding[f.key] || ""} onChange={(e) => setBranding({ ...branding, [f.key]: e.target.value })} className="bg-white/5 border-white/10 text-white" data-testid={`global-branding-${f.key}`} />
                )}
              </div>
            ))}
          </div>

          <div className="flex items-center gap-3 pt-4">
            <Button onClick={handleSave} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="save-global-branding">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {saving ? "Saving..." : "Save Global Branding"}
            </Button>
            {saved && <span className="text-sm text-amber-400 font-medium" data-testid="branding-save-success">Saved</span>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
